<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { CheckCircle, XCircle } from 'lucide-vue-next';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const isSuccess = ref<boolean | null>(null);
const isProcessing = ref(true);
const errorMessage = ref<string>('');

onMounted(async () => {
  const code = route.query.code as string;
  const error = route.query.error as string;

  if (error) {
    isSuccess.value = false;
    isProcessing.value = false;
    errorMessage.value = t('oauth_callback.error_auth_failed');
    return;
  }

  if (!code) {
    isSuccess.value = false;
    isProcessing.value = false;
    errorMessage.value = t('oauth_callback.error_no_code');
    return;
  }

  try {
    // Exchange the code for tokens on the backend
    // The backend handles the OAuth code exchange
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/google/callback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    const data = await response.json();

    if (response.ok && data.token) {
      localStorage.setItem('token', data.token);

      // Store setup token if provided
      if (data.setup_token) {
        localStorage.setItem('setup_token', data.setup_token);
      }

      isSuccess.value = true;

      // Redirect based on setup requirement
      setTimeout(() => {
        if (data.setup_token) {
          router.push('/auth/complete-setup');
        } else {
          router.push('/messenger');
        }
      }, 2000);
    } else {
      throw new Error(data.message || t('oauth_callback.error_generic'));
    }
  } catch (err: any) {
    console.error('OAuth callback error:', err);
    isSuccess.value = false;
    errorMessage.value = err.message || t('oauth_callback.error_generic');
  } finally {
    isProcessing.value = false;
  }
});
</script>

<template>
  <div class="flex min-h-screen bg-[#18181C] items-center justify-center p-6">
    <div
      class="w-full max-w-md bg-[#111113] rounded-3xl shadow-sm border border-[#2A2018] p-8 md:p-10"
    >
      <!-- Processing State -->
      <div v-if="isProcessing" class="text-center">
        <div
          class="inline-flex items-center justify-center w-20 h-20 bg-[#1E1E23] rounded-3xl mb-6"
        >
          <div
            class="w-10 h-10 border-4 border-[#2A2018] border-t-[#F5F0EA] rounded-full animate-spin"
          ></div>
        </div>
        <h2 class="text-2xl text-[#F5F0EA] mb-2">{{ $t('oauth_callback.processing_title') }}</h2>
        <p class="text-[#9E9080]">{{ $t('oauth_callback.processing_subtitle') }}</p>
      </div>

      <!-- Success State -->
      <div v-else-if="isSuccess === true" class="text-center">
        <div
          class="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-3xl mb-6"
        >
          <CheckCircle class="w-10 h-10 text-green-600" />
        </div>
        <h2 class="text-2xl text-[#F5F0EA] mb-2">{{ $t('oauth_callback.success_title') }}</h2>
        <p class="text-[#9E9080] mb-6">{{ $t('oauth_callback.success_subtitle') }}</p>
      </div>

      <!-- Error State -->
      <div v-else class="text-center">
        <div class="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-3xl mb-6">
          <XCircle class="w-10 h-10 text-red-600" />
        </div>
        <h2 class="text-2xl text-[#F5F0EA] mb-2">{{ $t('oauth_callback.error_title') }}</h2>
        <p class="text-[#9E9080] mb-6">{{ errorMessage }}</p>
        <button
          @click="router.push('/signup')"
          class="px-6 py-3 bg-[#0A0A0B] text-[#F5F0EA] rounded-2xl hover:bg-[#111113] transition"
        >
          {{ $t('oauth_callback.try_again') }}
        </button>
      </div>
    </div>
  </div>
</template>
